import { createServerFn } from "@tanstack/react-start";

import { CURRENCIES, FALLBACK_RATES, type CurrencyCode } from "@/lib/currency/config";

export type ExchangeRates = {
  base: "GBP";
  rates: Record<CurrencyCode, number>;
  fetchedAt: number;
  source: "live" | "fallback";
};

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
let cache: ExchangeRates | null = null;
let inFlight: Promise<ExchangeRates> | null = null;

function fallback(): ExchangeRates {
  return { base: "GBP", rates: { ...FALLBACK_RATES }, fetchedAt: Date.now(), source: "fallback" };
}

async function fetchLiveRates(): Promise<ExchangeRates> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch("https://open.er-api.com/v6/latest/GBP", { signal: controller.signal });
    if (!res.ok) throw new Error(`Rate fetch failed: ${res.status}`);
    const json = (await res.json()) as { result?: string; rates?: Record<string, number> };
    if (json.result !== "success" || !json.rates) throw new Error("Unexpected rate payload");
    const rates = { ...FALLBACK_RATES };
    for (const code of CURRENCIES) {
      const value = json.rates[code];
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        rates[code] = value;
      }
    }
    rates.GBP = 1;
    return { base: "GBP", rates, fetchedAt: Date.now(), source: "live" };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Server-side GBP-based exchange rates, in-memory cached for an hour with a
 * hardcoded fallback table if the upstream provider is unavailable. Display
 * only — never used for bidding, checkout, or Stripe amounts.
 */
export const getExchangeRates = createServerFn({ method: "GET" }).handler(async (): Promise<ExchangeRates> => {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache;
  }
  // Only uncached calls can reach the upstream provider — bound those.
  const { enforceRateLimit } = await import("@/lib/rate-limit.server");
  await enforceRateLimit("currency-rates", { anonPreset: "cachedRead", userPreset: "cachedRead" });

  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const rates = await fetchLiveRates();
      cache = rates;
      return rates;
    } catch {
      const rates = cache ?? fallback();
      cache = rates;
      return rates;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
});
